import type { Device } from '@studio/models/device';
import type { T_Maybe } from '@tryiris-ai/js-types';
import { broker } from './broker.js';
import { locationCache, ThumbnailConfig } from './constants.js';
import { selectLiveKitIdForDevice } from './select-livekit-id.js';
import type { LiveKit } from './types.js';
import { StudioError } from '#~/error/index.js';
import { locateProxy } from '#~/agent/utils.js';
import { toIdFromHex } from '#~/api/index.js';

export { selectLiveKitIdForDevice } from './select-livekit-id.js';

let thumbnailCanvas: T_Maybe<HTMLCanvasElement>;

/** @internal */
const getThumbnailCanvas = (): HTMLCanvasElement => {
  if (thumbnailCanvas) {
    return thumbnailCanvas;
  }

  thumbnailCanvas = document.createElement('canvas');

  thumbnailCanvas.width = ThumbnailConfig.width;
  thumbnailCanvas.height = ThumbnailConfig.height;

  return thumbnailCanvas;
};

/**
 * Capture a thumbnail of the specified device.
 *
 * @param deviceId - The ID of the device for which a thumbnail is to be captured.
 * @returns The thumbnail (`Blob`) of the specified device.
 */
export const captureThumbnail = async (deviceId: Device['id']): Promise<Blob> => {
  const proxy = locateProxy(deviceId);

  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  const [video] =
    locateLiveKit(deviceId)?.videoForAgent(proxy!.id, proxy!.embedded).get(deviceId)
      ?.attachedElements ?? [];
  const canvas = video ? getThumbnailCanvas() : undefined;
  const context = canvas?.getContext('2d');

  try {
    if (!video || !canvas || !context) {
      throw new Error('KX-1812');
    }

    context.drawImage(<HTMLVideoElement>video, 0, 0, canvas.width, canvas.height);

    // eslint-disable-next-line promise/avoid-new
    return await new Promise((resolve) => {
      canvas.toBlob(
        (result) => {
          if (!result) {
            throw new Error('CT-647');
          }

          resolve(result);
        },
        ThumbnailConfig.mimeType,
        ThumbnailConfig.quality
      );
    });
  } catch (cause) {
    throw new StudioError('Unable to capture thumbnail.', { cause });
  }
};

export const currentMode = (agentId: Uint8Array): 'cloud' | 'local' => {
  for (const liveKit of broker) {
    if (liveKit.modeForAgent(agentId) === 'local') {
      return liveKit.modeForAgent(agentId);
    }
  }

  return 'cloud';
};

/**
 * Locate the LiveKit connection proxy that is managing the the specified device.
 *
 * @param deviceId - Device identifier.
 * @returns `LiveKit` connection proxy or `undefined`.
 */
export const locateLiveKit = (deviceId: Device['id']): T_Maybe<LiveKit> => {
  if (!deviceId) {
    return undefined;
  }

  const proxy = locateProxy(deviceId);

  if (!proxy) {
    return undefined;
  }

  const id = toIdFromHex(proxy!.id)!;
  const mode = currentMode(id);

  let liveKitId = locationCache[mode].get(deviceId);

  // Do we already know where this device is?
  if (liveKitId) {
    const liveKit = broker.acquire(liveKitId);

    if (liveKit) {
      return liveKit;
    }

    locationCache[mode].delete(deviceId);
    liveKitId = undefined;
  }

  // We don't know where this device is, so we'll have to search for it.
  const candidates: Array<{ id: LiveKit['id']; mode: 'cloud' | 'local'; hasTrack: boolean }> = [];
  for (const liveKit of broker) {
    const agentMode = liveKit.modeForAgent(id);
    const hasTrack =
      liveKit.videoForAgent(proxy!.id, proxy!.embedded).has(deviceId) ||
      liveKit.audioForAgent(proxy!.id, proxy!.embedded).has(deviceId);
    candidates.push({ id: liveKit.id, mode: agentMode, hasTrack });
  }
  liveKitId = selectLiveKitIdForDevice(candidates, mode);
  if (liveKitId) {
    locationCache[mode].set(deviceId, liveKitId);
  }

  return broker.acquire(liveKitId);
};
